import React, { useCallback } from "react";
import styled from "styled-components";
import { FontSize, FontWeight, Spacing, content, ellipsis } from "@glossy";
import {
  Platform,
  isSocialInfluencerPlatform,
} from "@linkfluence/models/platform";
import Icon from "@ui-kit/icon";
import Portrait from "@ui-kit/identity/portrait";
import Link from "@ui-kit/utils/link";
import { Influencer } from "@radarly/models/influencer";
import { InfluencerLens } from "@radarly/models/influencer/generic";

interface IProps {
  influencerLens: InfluencerLens<Influencer>;
}

const NAME_MAX_WIDTH = "100%";

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.ExtraSmall};
  a {
    color: inherit;
  }
`;

const NameLink = styled(Link)`
  max-width: 100%;
`;

const StyledName = styled.span`
  ${ellipsis(NAME_MAX_WIDTH)};
  display: block;
  font-weight: ${FontWeight.Bold};
  color: ${content};
  font-size: ${FontSize.ExtraLarge};
  line-height: normal;
`;

const StyledScreenName = styled.span`
  ${ellipsis(NAME_MAX_WIDTH)};
  color: ${content};
  font-size: ${FontSize.Large};
  line-height: normal;
`;

const NameVerifiedCombo = styled.div`
  display: flex;
  gap: ${Spacing.ExtraSmall};
  align-items: center;
`;

const RadarlyAuthorHead = ({ influencerLens }: IProps) => {
  const name = influencerLens.getName();
  const screenName = influencerLens.getScreenName();
  const avatar = influencerLens.getAvatar();
  const permalink = influencerLens.getPermalink();
  const verified = influencerLens.isVerified();
  const platform = influencerLens.getPlatform();

  const isSocial = isSocialInfluencerPlatform(platform);
  const isReddit = influencerLens.isPlatform(Platform.Reddit);

  const stopPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <Root>
      {/* Avatar */}
      <Link url={permalink} target="_blank" onClick={stopPropagation}>
        <Portrait type="account" picture={avatar} size={108} />
      </Link>

      {/* Name */}
      <NameLink url={permalink} target="_blank" onClick={stopPropagation}>
        <NameVerifiedCombo>
          <StyledName>{name ? name : screenName}</StyledName>
          {!!verified && <Icon color="#3C8DB6" glyph="verified" />}
        </NameVerifiedCombo>
      </NameLink>

      {/* Screenname */}
      {screenName && (
        <StyledScreenName>
          {isSocial && !isReddit ? `@${screenName}` : screenName}
        </StyledScreenName>
      )}
    </Root>
  );
};

export default RadarlyAuthorHead;
